import React, { useState } from 'react';
import { useNavigation } from '../../context/NavigationContext';
import { legalConfig } from '../../../config/legalConfig';
import {
  Sparkles,
  ShieldCheck,
  Mail,
  Phone,
  MapPin,
  CheckCircle2,
  Send,
  Lock,
} from 'lucide-react';

export const PublicFooter: React.FC = () => {
  const { navigate } = useNavigation();
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      setSubscribed(true);
      setEmail('');
    }
  };

  return (
    <footer className="bg-slate-950 border-t border-slate-800 text-slate-400 text-sm">
      {/* Top Banner / Callout */}
      <div className="border-b border-slate-800/80 bg-gradient-to-b from-slate-900/40 to-transparent py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-blue-600/10 border border-blue-500/20 flex items-center justify-center text-blue-400 shrink-0">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <div>
              <h4 className="text-white font-semibold text-base">
                İşletmenizin Dijital Vitrinini Bugün Oluşturun
              </h4>
              <p className="text-slate-400 text-xs mt-0.5">
                Dakikalar içinde sitenizi kurun, yerel AI görünürlüğünüzü ölçün ve dijital varlığınızı büyütün.
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/kayit')}
            className="px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-semibold text-xs sm:text-sm shadow-lg shadow-blue-600/25 transition whitespace-nowrap active:scale-95"
          >
            Hemen Başlayın
          </button>
        </div>
      </div>

      {/* Main Footer Links */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          {/* Brand & Corporate Structure Col */}
          <div className="lg:col-span-2 space-y-4">
            <div
              onClick={() => navigate('/')}
              className="flex items-center gap-3 cursor-pointer group"
            >
              <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-blue-600 via-indigo-600 to-sky-400 flex items-center justify-center shadow-md shadow-blue-600/30">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xl font-bold tracking-tight text-white">
                  Adio<span className="text-blue-400">soft</span>
                </span>
                <span className="text-[10px] uppercase font-bold tracking-widest px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">
                  Platform
                </span>
              </div>
            </div>

            <p className="text-slate-400 text-xs sm:text-sm leading-relaxed max-w-sm">
              {legalConfig.relationshipStatement}
            </p>

            <div className="pt-2 flex flex-col gap-2 text-xs text-slate-400">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-slate-500 shrink-0" />
                <span>Hukuki İşletmeci: <strong className="text-slate-300">{legalConfig.companyName}</strong></span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-slate-500 shrink-0" />
                <a href="mailto:info@adiosoft.com" className="hover:text-white transition">info@adiosoft.com</a>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-slate-500 shrink-0" />
                <a href="tel:+905323712548" className="hover:text-white transition">0532 371 2548</a>
              </div>
            </div>
          </div>

          {/* Kolon 1: Ürünler */}
          <div>
            <h5 className="text-white font-semibold text-xs sm:text-sm mb-4 tracking-wide uppercase font-mono">
              Ürünler
            </h5>
            <ul className="space-y-2.5 text-xs sm:text-sm">
              <li>
                <button
                  onClick={() => navigate('/kayit')}
                  className="hover:text-white transition-colors text-left"
                >
                  Web Sitesi Oluştur
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    navigate('/');
                    setTimeout(() => {
                      document.getElementById('ai-visibility')?.scrollIntoView({ behavior: 'smooth' });
                    }, 100);
                  }}
                  className="hover:text-white transition-colors text-left"
                >
                  AI Local Visibility
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate('/temalar')}
                  className="hover:text-white transition-colors text-left"
                >
                  Hazır Temalar
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate('/fiyatlandirma')}
                  className="hover:text-white transition-colors text-left"
                >
                  Fiyatlandırma & Paketler
                </button>
              </li>
            </ul>
          </div>

          {/* Kolon 2: Kurumsal & Hukuki */}
          <div>
            <h5 className="text-white font-semibold text-xs sm:text-sm mb-4 tracking-wide uppercase font-mono">
              Kurumsal & Hukuk
            </h5>
            <ul className="space-y-2.5 text-xs sm:text-sm">
              <li>
                <button
                  onClick={() => navigate('/iletisim')}
                  className="hover:text-white transition-colors text-left"
                >
                  Bize Ulaşın / İletişim
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate('/kullanim-kosullari')}
                  className="hover:text-white transition-colors text-left text-slate-300"
                >
                  Kullanım Koşulları
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate('/gizlilik-politikasi')}
                  className="hover:text-white transition-colors text-left text-slate-300"
                >
                  Gizlilik Politikası
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate('/kvkk-aydinlatma-metni')}
                  className="hover:text-white transition-colors text-left text-slate-300"
                >
                  KVKK Aydınlatma Metni
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate('/cerez-politikasi')}
                  className="hover:text-white transition-colors text-left text-slate-300"
                >
                  Çerez Politikası
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate('/mesafeli-satis-ve-on-bilgilendirme')}
                  className="hover:text-white transition-colors text-left text-slate-300"
                >
                  Mesafeli Satış ve Ön Bilgilendirme
                </button>
              </li>
            </ul>
          </div>

          {/* Kolon 3: Hesap */}
          <div>
            <h5 className="text-white font-semibold text-xs sm:text-sm mb-4 tracking-wide uppercase font-mono">
              Hesap
            </h5>
            <ul className="space-y-2.5 text-xs sm:text-sm">
              <li>
                <button
                  onClick={() => navigate('/giris')}
                  className="hover:text-white transition-colors text-left"
                >
                  Kullanıcı Girişi
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate('/kayit')}
                  className="hover:text-white transition-colors text-left text-blue-400 font-medium"
                >
                  Hesap Oluştur
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="hover:text-white transition-colors text-left"
                >
                  Müşteri Paneli
                </button>
              </li>
            </ul>

            {/* Newsletter mini */}
            <div className="mt-6 pt-4 border-t border-slate-800/80">
              <span className="text-[11px] font-semibold text-slate-300 block mb-2">Bülten Aboneliği</span>
              {subscribed ? (
                <div className="text-[11px] text-emerald-400 flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                  <span>Kayıt tamamlandı.</span>
                </div>
              ) : (
                <form onSubmit={handleSubscribe} className="space-y-1.5">
                  <div className="relative">
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="e-posta adresiniz"
                      className="w-full px-3 py-1.5 bg-slate-900 border border-slate-800 rounded-lg text-xs text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                    />
                    <button
                      type="submit"
                      className="absolute right-1 top-1 bottom-1 px-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded text-[10px] font-semibold transition"
                    >
                      Kayıt
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Legal Copyright & Links */}
      <div className="border-t border-slate-800/80 py-6 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs">
          <div className="flex flex-col sm:flex-row items-center gap-2 text-slate-500 text-center sm:text-left">
            <span>© 2026 Adiosoft. {legalConfig.companyName} bünyesinde sunulmaktadır. Tüm hakları saklıdır.</span>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-slate-400 text-xs">
            <button
              onClick={() => navigate('/kullanim-kosullari')}
              className="hover:text-white transition"
            >
              Kullanım Koşulları
            </button>
            <button
              onClick={() => navigate('/gizlilik-politikasi')}
              className="hover:text-white transition"
            >
              Gizlilik Politikası
            </button>
            <button
              onClick={() => navigate('/kvkk-aydinlatma-metni')}
              className="hover:text-white transition"
            >
              KVKK
            </button>
            <button
              onClick={() => navigate('/cerez-politikasi')}
              className="hover:text-white transition"
            >
              Çerezler
            </button>
            <button
              onClick={() => navigate('/mesafeli-satis-ve-on-bilgilendirme')}
              className="hover:text-white transition"
            >
              Mesafeli Satış
            </button>
            <button
              onClick={() => navigate('/giris')}
              className="text-blue-400 hover:text-blue-300 font-medium transition"
            >
              Giriş Yap
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};
export default PublicFooter;
